import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Building2, RefreshCw, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';

const accountStatusClass = (value = '') => {
  const status = String(value).toLowerCase();
  if (['active', 'activated'].includes(status)) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status.includes('clarification')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'removed' || status === 'failed') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

export default function AdminLinkedAccounts() {
  const { user } = useAuth();
  const [institutions, setInstitutions] = useState([]);
  const [razorpayAccounts, setRazorpayAccounts] = useState([]);
  const [keyId, setKeyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [removingId, setRemovingId] = useState('');
  const [lookupAccountId, setLookupAccountId] = useState('');
  const [lookupProductId, setLookupProductId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const canEditLinkedAccounts = hasPermission(user, PERMISSIONS.LINKED_ACCOUNTS_EDIT);

  const fetchLinkedAccounts = async () => {
    try {
      setLoading(true);
      const [localRes, razorpayRes] = await Promise.all([
        axios.get('/api/admin/institutions?export=true'),
        axios.get('/api/admin/linked-accounts/razorpay'),
      ]);
      setInstitutions(localRes.data?.items || localRes.data || []);
      setRazorpayAccounts(razorpayRes.data?.accounts || []);
      setKeyId(razorpayRes.data?.keyId || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load linked accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedAccounts();
  }, []);

  const localRows = useMemo(() => institutions
    .map((institution) => {
      const account = institution.bankAccount || {};
      return {
        institution,
        account,
        accountId: account.razorpayAccountId || institution.razorpayAccountId || '',
        productId: account.razorpayProductId || institution.razorpayProductId || '',
        status: account.verificationStatus || institution.razorpayProductStatus || institution.razorpayAccountStatus || 'not submitted',
      };
    })
    .filter((row) => row.account?.id || row.account?.verificationStatus), [institutions]);

  const rows = useMemo(() => {
    const mappedLiveRows = razorpayAccounts.map(({ account = {}, institution, bankAccount }) => ({
      source: 'razorpay',
      institution,
      account: bankAccount || {},
      accountId: account.id || '',
      productId: bankAccount?.razorpayProductId || institution?.razorpayProductId || '',
      status: account.status || bankAccount?.verificationStatus || institution?.razorpayAccountStatus || 'unknown',
      beneficiaryName: account.customer_facing_business_name || account.legal_business_name || bankAccount?.beneficiaryName || '',
      email: account.email || '',
      phone: account.phone || '',
      rawAccount: account,
    }));
    const liveIds = new Set(mappedLiveRows.map((row) => row.accountId).filter(Boolean));
    const localOnlyRows = localRows
      .filter((row) => row.accountId && !liveIds.has(row.accountId))
      .map((row) => ({ ...row, source: 'database', beneficiaryName: row.account.beneficiaryName || '' }));

    return [...mappedLiveRows, ...localOnlyRows]
    .filter((row) => {
      const haystack = [
        row.institution?.collegeName,
        row.institution?.displayName,
        row.accountId,
        row.productId,
        row.beneficiaryName,
        row.email,
        row.phone,
      ].join(' ').toLowerCase();
      return haystack.includes(searchTerm.toLowerCase());
    });
  }, [localRows, razorpayAccounts, searchTerm]);

  const handleRemove = async (institution) => {
    const name = institution.displayName || institution.collegeName;
    const confirmed = window.confirm(`Remove linked account for ${name}? Split payments will be disabled.`);
    if (!confirmed) return;
    try {
      setRemovingId(institution._id || institution.id);
      await axios.delete(`/api/admin/institutions/${institution._id || institution.id}/linked-account`);
      toast.success('Linked account removed');
      await fetchLinkedAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove linked account');
    } finally {
      setRemovingId('');
    }
  };

  const handleLookup = async () => {
    const accountId = lookupAccountId.trim();
    if (!accountId) {
      toast.error('Enter a Razorpay account ID');
      return;
    }
    try {
      setLookupLoading(true);
      const res = await axios.get(`/api/admin/linked-accounts/lookup/${encodeURIComponent(accountId)}`, {
        params: lookupProductId.trim() ? { productId: lookupProductId.trim() } : {},
      });
      setLookupResult(res.data);
    } catch (err) {
      setLookupResult(null);
      toast.error(err.response?.data?.message || 'Failed to fetch Razorpay account');
    } finally {
      setLookupLoading(false);
    }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Linked Accounts</h1>
          <p className="text-slate-500 mt-1">Live Razorpay accounts for key {keyId || 'not configured'}, with local remove mapping actions.</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search account or institution..."
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none w-72"
            />
          </div>
          <button onClick={fetchLinkedAccounts} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:bg-slate-50">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Razorpay Account ID</label>
            <input
              value={lookupAccountId}
              onChange={(e) => setLookupAccountId(e.target.value)}
              placeholder="acc_xxxxxxxxxxxxxx"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Route Product ID</label>
            <input
              value={lookupProductId}
              onChange={(e) => setLookupProductId(e.target.value)}
              placeholder="Optional product id"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
            />
          </div>
          <button
            onClick={handleLookup}
            disabled={lookupLoading}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            <KeyRound size={17} />
            {lookupLoading ? 'Fetching...' : 'Fetch From Razorpay'}
          </button>
        </div>

        {lookupResult && (
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Account</p>
              <p className="mt-2 font-mono text-sm font-bold text-slate-800 break-all">{lookupResult.account?.id || '-'}</p>
              <p className="mt-1 text-sm text-slate-600">{lookupResult.account?.email || lookupResult.account?.phone || 'No contact returned'}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Status</p>
              <span className={`mt-2 inline-flex px-2.5 py-1 rounded-lg border text-xs font-bold capitalize ${accountStatusClass(lookupResult.product?.activation_status || lookupResult.account?.status)}`}>
                {String(lookupResult.product?.activation_status || lookupResult.account?.status || 'unknown').replace(/_/g, ' ')}
              </span>
              <p className="mt-2 font-mono text-xs text-slate-500 break-all">{lookupResult.product?.id || 'No product requested in lookup'}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Local Mapping</p>
              {lookupResult.institution ? (
                <>
                  <p className="mt-2 text-sm font-bold text-slate-800">{lookupResult.institution.displayName || lookupResult.institution.collegeName}</p>
                  {canEditLinkedAccounts && (
                    <button
                      onClick={() => handleRemove(lookupResult.institution)}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-xs font-bold text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-50"
                    >
                      <Trash2 size={15} />
                      Remove Mapping
                    </button>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm font-semibold text-amber-600">Not mapped in local database</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
                <th className="p-4 font-bold">Institution</th>
                <th className="p-4 font-bold">Source</th>
                <th className="p-4 font-bold">Beneficiary</th>
                <th className="p-4 font-bold">Status</th>
                <th className="p-4 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-10 text-center text-slate-500">No linked accounts found.</td>
                </tr>
              ) : rows.map(({ source, institution, account, status, beneficiaryName, email, phone }) => (
                <tr key={`${source}-${institution?._id || institution?.id || beneficiaryName}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                        <Building2 size={18} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{institution ? (institution.displayName || institution.collegeName) : 'Not mapped locally'}</p>
                        <p className="text-xs text-slate-500">{institution?.slug || source}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-slate-600 capitalize">{source || 'database'}</td>
                  <td className="p-4">
                    <p className="font-semibold text-sm text-slate-700">{beneficiaryName || account.beneficiaryName || '-'}</p>
                    <p className="text-xs text-slate-500">{account.accountNumber ? `A/C ${account.accountNumber}` : (email || phone || '')}</p>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-lg border text-xs font-bold capitalize ${accountStatusClass(status)}`}>
                      {String(status).replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    {institution && canEditLinkedAccounts ? (
                      <button
                        onClick={() => handleRemove(institution)}
                        disabled={removingId === (institution._id || institution.id)}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-50 disabled:opacity-60"
                      >
                        <Trash2 size={15} />
                        {removingId === (institution._id || institution.id) ? 'Removing...' : 'Remove'}
                      </button>
                    ) : (
                      <span className="text-xs font-bold text-slate-400">No local mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
